import { h } from 'preact'
import { StateUpdater, useState } from 'preact/hooks'
import { initI18n } from 'i18n/i18n'

import { CurrencyValueForm } from 'models/apps'
import { SpinnerError, CurrencyInput } from 'models/calculator'
import { Calculation } from 'models/exchange-transfer-calculate'

import {
  DEFAULT_EXCHANGE_DATA,
  DEFAULT_RESPONSE,
  DISCOUNT_RATE,
  DEFAULT_MINIMAL_AMOUNT,
  PLN_MINIMAL_AMOUNT,
  SETTINGS_KEYS,
  DEFAULT_REFRESH_TIME,
  WITHOUT_TRANSFER_FEE,
  DEFAULT_CURRENCY,
  DOUBLE_INPUT_MODE,
} from './config'
import { DEFAULT_DEBOUNCE_TIME } from 'apps-calculators/config'

import { getCurrentLanguage } from 'utils/current-language'
import { formatCurrencyAmount } from 'apps-utils/validate-currency-input'
import { validateDiscountRate } from 'apps-utils/validate-discount'
import { getCurrencySelectOptions } from 'apps-utils/get-currency-select-options'
import { getIsBelowMinimalAmount, getMinimalAmountForCurrency } from 'apps-utils/get-is-below-minimal-amount'

import { useExchangeData } from 'hooks/useExchangeData'
import { useDefaultBaseAmount } from 'hooks/useDefaultBaseAmount'
import { useSettings } from 'hooks/useSettings'
import { useSyncExchangeAmount } from './hooks/useSyncExchangeAmount'
import { useSyncCurrenciesWithSlug } from './hooks/useSyncCurrenciesWithSlug'

import { ErrorSpinner } from 'common-modules/feedback/error-spinner'
import { CalculatorErrorInfo } from 'common-modules/feedback/calculator-error-info'

import SwapButton from './components/swap-button'
import ExchangeButtons from './components/exchange-buttons'
import CurrencyInputGroup from './components/currency-input-group'

initI18n()

const DoubleInputCalculator = () => {
  const [baseCurrency, setBaseCurrency] = useState(DEFAULT_EXCHANGE_DATA.baseCurrency)
  const [counterCurrency, setCounterCurrency] = useState(DEFAULT_EXCHANGE_DATA.counterCurrency)

  const [baseValue, setBaseValue] = useState<CurrencyValueForm>(
    formatCurrencyAmount(DEFAULT_EXCHANGE_DATA.baseAmount || '0'),
  )
  const [counterValue, setCounterValue] = useState<CurrencyValueForm>(
    formatCurrencyAmount(DEFAULT_EXCHANGE_DATA.counterAmount || '0'),
  )

  const [activeCurrencyInput, setActiveCurrencyInput] = useState<CurrencyInput>('base')
  const [exchangeData, setExchangeData] = useState<Calculation>(DEFAULT_RESPONSE)
  const [spinnerError, setSpinnerError] = useState<SpinnerError>({ isErrorVisible: false })
  const currentLang = getCurrentLanguage()
  const [discountRate] = useState<number>(validateDiscountRate(DISCOUNT_RATE))
  const [isRotated, setIsRotated] = useState(false)

  const {
    isSettingsLoading,
    isCurrentCurrencySupported,
    isCurrencyPairSupported,
    setIsCurrentCurrencySupported,
    supportedCurrenciesPairs,
    supportedCurrenciesPairsAllCombinations,
    minimalAmounts,
    exchangeRatePrecision,
  } = useSettings({
    setSpinnerError,
    config: {
      SETTINGS_KEYS,
      DEFAULT_MINIMAL_AMOUNT,
      PLN_MINIMAL_AMOUNT,
      DEFAULT_CURRENCY,
    },
  })

  useSyncCurrenciesWithSlug({
    supportedCurrenciesPairs,
    baseCurrency,
    counterCurrency,
    setBaseCurrency,
    setCounterCurrency,
  })

  const effectiveBaseValue = useDefaultBaseAmount({
    baseCurrency,
    counterCurrency,
    exchangeRatePrecision,
    minimalAmounts,
    defaultBaseAmount: DEFAULT_EXCHANGE_DATA.baseAmount,
    value: baseValue,
    setValue: setBaseValue,
  })

  const isBaseInputActive = activeCurrencyInput === 'base'
  const activeValue = isBaseInputActive ? effectiveBaseValue : counterValue
  const activeDirection = isBaseInputActive ? 'forward' : 'back'
  const activeCurrency = isBaseInputActive ? baseCurrency : counterCurrency

  const isBelowMinimalAmount = getIsBelowMinimalAmount({
    value: activeValue,
    minimalAmounts,
    baseCurrency,
    counterCurrency,
    direction: activeDirection,
    activeCurrency,
  })

  const { baseCurrencyOptions, counterCurrencyOptions } = getCurrencySelectOptions({
    baseCurrency,
    counterCurrency,
    supportedCurrenciesPairsAllCombinations,
    isSettingsLoading,
  })

  useExchangeData({
    isSettingsLoading,
    direction: activeDirection,
    baseCurrency,
    counterCurrency,
    value: activeValue,
    discountRate,
    supportedCurrenciesPairs,
    setExchangeData,
    setSpinnerError,
    isCurrencyPairSupported,
    setIsCurrentCurrencySupported,
    isBelowMinimalAmount,
    config: {
      DEFAULT_RESPONSE,
      DEFAULT_REFRESH_TIME,
      DEFAULT_EXCHANGE_DATA,
      WITHOUT_TRANSFER_FEE,
      DOUBLE_INPUT_MODE,
    },
  })

  useSyncExchangeAmount({
    exchangeData,
    activeCurrencyInput,
    baseValue,
    counterValue,
    setBaseValue,
    setCounterValue,
  })

  const handleSwapCurrencies = () => {
    setIsRotated((prev) => !prev)
    setBaseCurrency(counterCurrency)
    setCounterCurrency(baseCurrency)
    setBaseValue(counterValue)
    setCounterValue(baseValue)
    setActiveCurrencyInput((prev) => (prev === 'base' ? 'counter' : 'base'))
  }

  const handleBaseValueChange: StateUpdater<CurrencyValueForm> = (value) => {
    setActiveCurrencyInput('base')
    setBaseValue(value)
  }

  const handleCounterValueChange: StateUpdater<CurrencyValueForm> = (value) => {
    setActiveCurrencyInput('counter')
    setCounterValue(value)
  }

  return (
    <div className="double-input-calculator">
      <div className="double-input-calculator__main">
        <CurrencyInputGroup
          inputId="currency-rate-calculator-base-input"
          labelKey="rateCalculator.baseInputLabel"
          value={baseValue}
          onValueChange={handleBaseValueChange}
          isBelowMinimalAmount={isBaseInputActive && isBelowMinimalAmount}
          isBaseCurrencyComponent={true}
          debounceTime={DEFAULT_DEBOUNCE_TIME}
          baseCurrency={baseCurrency}
          counterCurrency={counterCurrency}
          setBaseCurrency={setBaseCurrency}
          setCounterCurrency={setCounterCurrency}
          fixedCurrencyList={baseCurrencyOptions}
        />

        <SwapButton
          isRotated={isRotated}
          onClick={handleSwapCurrencies}
        />

        <CurrencyInputGroup
          inputId="currency-rate-calculator-counter-input"
          labelKey="rateCalculator.counterInputLabel"
          value={counterValue}
          onValueChange={handleCounterValueChange}
          isBelowMinimalAmount={!isBaseInputActive && isBelowMinimalAmount}
          isBaseCurrencyComponent={false}
          debounceTime={DEFAULT_DEBOUNCE_TIME}
          baseCurrency={baseCurrency}
          counterCurrency={counterCurrency}
          setBaseCurrency={setBaseCurrency}
          setCounterCurrency={setCounterCurrency}
          fixedCurrencyList={counterCurrencyOptions}
          exchangeData={exchangeData}
        />

        <CalculatorErrorInfo
          isBelowMinimalAmount={isBelowMinimalAmount}
          isCurrentCurrencySupported={isCurrentCurrencySupported}
          minimalAmount={getMinimalAmountForCurrency(minimalAmounts, activeCurrency)}
          minimalAmountCurrency={activeCurrency}
          customClassName="double-input-calculator__error-info"
        />
      </div>

      <ExchangeButtons showAlert={currentLang !== 'pl'} />

      {spinnerError.isErrorVisible && <ErrorSpinner errorText={spinnerError.message} />}
    </div>
  )
}

export default DoubleInputCalculator
